import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { UclThemeProvider } from "src/components/ucl-theme-provider";
import { SuccessCard } from "src/components/results-cards/success-card/success-card";
import { DeclinedCard } from "src/components/results-cards/declined-card/declined-card";
import { EligibilityResultType } from "src/types/general";
import styles from "./results-page.module.scss";

export function ResultsPage({ result, customerName }: Readonly<EligibilityResultType>) {
    const { t } = useTranslation();
    const isApproved = result?.decision === "APPROVED";
    const fordMotorUrl = import.meta.env.VITE_FORD_MOTOR_URL;

    const { dealerConfig, promotionsConfig, rangeConfig } = useMemo(() => {
        const dealer = t(
            isApproved ? "creditDecision.links.contactDealerApproved" : "creditDecision.links.contactDealerReferred",
            { baseUrl: fordMotorUrl, interpolation: { escapeValue: false }, returnObjects: true }
        ) as {
            url: string;
            openNewTab: boolean;
        };

        const promotions = t("creditDecision.links.promotions", { returnObjects: true }) as {
            url: string;
            openNewTab: boolean;
        };

        const range = t("creditDecision.links.range", {
            baseUrl: fordMotorUrl,
            interpolation: { escapeValue: false },
            returnObjects: true,
        }) as {
            url: string;
            openNewTab: boolean;
        };

        return {
            dealerConfig: dealer,
            promotionsConfig: promotions,
            rangeConfig: range,
        };
    }, [t, isApproved, fordMotorUrl]);

    return (
        <UclThemeProvider>
            <div className={styles.pageContainer}>
                <Helmet>
                    <title>{t("pageTitle.eligibilityDecision")}</title>
                </Helmet>

                {isApproved ? (
                    <SuccessCard
                        customerName={customerName}
                        dealerConfig={dealerConfig}
                        promotionsConfig={promotionsConfig}
                    />
                ) : (
                    <DeclinedCard dealerConfig={dealerConfig} rangeConfig={rangeConfig} />
                )}

                <div data-testid="importantInfo" className={styles.importantInfoContainer}>
                    <p className={styles.importantInfoLabel}>{t("general.importantInfoLabel")}</p>
                    <p className={styles.importantInfoText}>{t("creditDecision.importantInfo")}</p>
                </div>
            </div>
        </UclThemeProvider>
    );
}
